import axios from "axios";

import { getBaseUrl } from "./base-url";
import { prepareOAuthNonce } from "./oauth-handoff";

/**
 * MOSA-146. Per-user Google Sheets sign-in.
 *
 * The consent flow is server-side (ARD section 1): the app asks the API for the
 * consent URL with its normal auth header, then hands the browser to Google.
 * The API redirects back to the app with `?google_sheets=<status>`.
 *
 * Missing OAuth config is a normal state, not an error. The connect form shows
 * "Google sign-in is not configured yet" and every other flow keeps working.
 */

const ALLOWED_RETURN_PATHS = [
  "/",
  "/setup",
  "/datasources",
  "/datasources/new",
  "/connect/sheets",
  "/data-sources",
];

export type SheetsPickerConfig = {
  apiKey: string;
  projectNumber: string;
  scope: string;
};

export type SheetsStartResult =
  | { status: "ready"; url: string; picker: SheetsPickerConfig | null }
  | { status: "not_configured"; message: string }
  | { status: "signup_required"; message: string }
  | { status: "error"; message: string };

export type SheetsCallbackStatus =
  | "connected"
  | "cancelled"
  | "failed"
  | "not_configured"
  | "reconnect_required"
  /** MOSA-468. The Google consent screen returned a grant without drive.file. */
  | "scope_missing"
  /** MOSA-468. A Google Workspace admin policy blocks the app. */
  | "admin_policy_enforced"
  /**
   * MOSA-407. The Google email already has an OnlyDash account with a
   * password. Nothing was created or changed: the user signs in with that
   * password once, and the next consent links Google to it.
   */
  | "link_required";

const STATUS_COPY: Record<SheetsCallbackStatus, string> = {
  connected: "Google account connected.",
  cancelled: "Google sign-in was cancelled. Nothing changed.",
  failed: "Google sign-in did not finish. Try again.",
  not_configured: "Google sign-in is not configured yet.",
  reconnect_required: "Google access expired. Connect Google again.",
  scope_missing:
    "Google did not give access to your files. Tick the Drive file box and try again.",
  admin_policy_enforced:
    "Your Google Workspace blocks OnlyDash. Your Workspace admin must allow it.",
  link_required:
    "This email already has an account. Sign in with your password once to link Google.",
};

export const isSheetsCallbackStatus = (
  value: string | null,
): value is SheetsCallbackStatus =>
  value !== null && Object.prototype.hasOwnProperty.call(STATUS_COPY, value);

export const sheetsStatusMessage = (status: SheetsCallbackStatus): string =>
  STATUS_COPY[status];

const PANEL_PATH =
  /^\/datasources\/[A-Za-z0-9_-]{1,128}\/collections\/(?!\.{1,2}$)[A-Za-z0-9_%.-]{1,256}$/;

export const currentReturnPath = (): string => {
  if (typeof window === "undefined") {
    return "/";
  }
  const hash = window.location.hash.replace(/^#/, "");
  const path = hash.split("?")[0];
  if (PANEL_PATH.test(path)) {
    return path;
  }
  return ALLOWED_RETURN_PATHS.includes(path) ? path : "/";
};

/**
 * MOSA-467. The wizard session from the URL, when there is one. The OAuth
 * `state` carries it, so the callback returns to the same session. The API
 * checks the format again; this only picks a clean value.
 */
export const currentWizardSession = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const hash = window.location.hash.replace(/^#/, "");
  const index = hash.indexOf("?");
  if (index === -1) {
    return null;
  }
  const value = new URLSearchParams(hash.slice(index + 1)).get("session");
  return value && /^[A-Za-z0-9_-]{1,128}$/.test(value) ? value : null;
};

/**
 * Ask the API for the consent URL. Only a signed-up user gets one: the API
 * answers 403 signup_required for a guest (PRD Q8).
 */
export const startSheetsSignIn = async (
  token: string | null,
  returnPath?: string,
  session?: string | null,
  /** MOSA-407. Preselect this Google account on the consent screen. */
  loginHint?: string | null,
): Promise<SheetsStartResult> => {
  try {
    // Bind the consent to this tab: the callback ticket redeems with the nonce.
    const { nonceHash } = await prepareOAuthNonce();
    // API #51: both outcomes are HTTP 200. When Google OAuth is not set up the
    // body is `{ configured: false, reason: 'google_not_configured' }` (no 500).
    const response = await axios.post<{
      configured?: boolean;
      url?: string;
      reason?: string;
      picker?: SheetsPickerConfig;
    }>(
      `${getBaseUrl()}/auth/google/sheets/start`,
      {
        returnPath: returnPath ?? currentReturnPath(),
        nonceHash,
        ...(session ? { session } : {}),
        ...(loginHint ? { loginHint } : {}),
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
    const data = response.data;
    if (!data?.configured || !data.url) {
      return {
        status: "not_configured",
        message: "Google sign-in is not configured yet.",
      };
    }
    return {
      status: "ready",
      url: data.url,
      picker: data.picker?.apiKey ? data.picker : null,
    };
  } catch (error) {
    const body = (
      error as {
        response?: {
          status?: number;
          data?: { code?: string; reason?: string; message?: string };
        };
      }
    )?.response;
    if (body?.status === 403 && body.data?.code === "signup_required") {
      return {
        status: "signup_required",
        message:
          "Sign up to connect a Google Sheet. Guests can try the demo.",
      };
    }
    // Pre-#51 answers used 503 or 500 with this code. Treat them as the same
    // normal "not configured" state so the form shows a status line, not an
    // error alert.
    const notConfigured =
      body?.status === 503 ||
      body?.data?.code === "google_not_configured" ||
      body?.data?.reason === "google_not_configured";
    if (notConfigured) {
      return {
        status: "not_configured",
        message: "Google sign-in is not configured yet.",
      };
    }
    return {
      status: "error",
      message: body?.data?.message || "Could not start Google sign-in.",
    };
  }
};

/**
 * MOSA-407. The merged sign-in: one Google screen that both creates (or signs
 * in to) the OnlyDash account and grants `drive.file`. This is what the wizard's
 * first step calls, signed in or not, so the Sheets path never shows a separate
 * sign-up page.
 *
 * There is no `signup_required` outcome here by design: a caller with no
 * account is exactly who this route is for.
 */
export const startMergedGoogleSignIn = async (
  token: string | null,
  returnPath?: string,
  session?: string | null,
  loginHint?: string | null,
): Promise<SheetsStartResult> => {
  try {
    const { nonceHash } = await prepareOAuthNonce();
    const response = await axios.post<{
      configured?: boolean;
      url?: string;
      reason?: string;
      picker?: SheetsPickerConfig;
    }>(
      `${getBaseUrl()}/auth/google/sheets/sign-in-start`,
      {
        returnPath: returnPath ?? currentReturnPath(),
        nonceHash,
        ...(session ? { session } : {}),
        ...(loginHint ? { loginHint } : {}),
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
    const data = response.data;
    if (!data?.configured || !data.url) {
      return {
        status: "not_configured",
        message: "Google sign-in is not configured yet.",
      };
    }
    return {
      status: "ready",
      url: data.url,
      picker: data.picker?.apiKey ? data.picker : null,
    };
  } catch (error) {
    const body = (
      error as {
        response?: {
          status?: number;
          data?: { code?: string; reason?: string; message?: string };
        };
      }
    )?.response;
    const notConfigured =
      body?.status === 503 ||
      body?.data?.code === "google_not_configured" ||
      body?.data?.reason === "google_not_configured";
    if (notConfigured) {
      return {
        status: "not_configured",
        message: "Google sign-in is not configured yet.",
      };
    }
    return {
      status: "error",
      message: body?.data?.message || "Could not start Google sign-in.",
    };
  }
};

/** The switch path for an existing service-account sheet (PRD Q10). */
export const switchSheetsToSignIn = async (
  token: string | null,
  datasourceId: string,
  googleConnectionId: string,
  spreadsheetId: string,
): Promise<{ ok: true } | { ok: false; message: string }> => {
  try {
    await axios.post(
      `${getBaseUrl()}/datasources/${datasourceId}/google/sign-in`,
      { googleConnectionId, spreadsheetId },
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
    return { ok: true };
  } catch (error) {
    const data = (
      error as { response?: { data?: { message?: string } } }
    )?.response?.data;
    return {
      ok: false,
      message: data?.message || "Could not switch this sheet to Google sign-in.",
    };
  }
};

/**
 * True when a reconnect is needed, from a typed API error. The API sends
 * `code: google_reconnect_required` (expired/revoked) or
 * `google_file_access_lost` (this one file was unshared).
 */
export const sheetsAuthError = (
  error: unknown,
): { kind: "reconnect" | "lost_file"; message: string } | null => {
  const data = (
    error as { response?: { data?: { code?: string; message?: string } } }
  )?.response?.data;
  if (!data?.code) {
    return null;
  }
  if (data.code === "google_reconnect_required") {
    return {
      kind: "reconnect",
      message: data.message || "Reconnect Google to keep editing this sheet.",
    };
  }
  if (data.code === "google_file_access_lost") {
    return {
      kind: "lost_file",
      message:
        data.message ||
        "This Google Sheet is no longer shared with the connected account.",
    };
  }
  return null;
};
